import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  addQuarters,
  subQuarters,
  addYears,
  subYears,
  addDays,
  subDays,
  getISOWeek,
  isSameDay,
  isSameMonth,
  differenceInCalendarDays,
} from "date-fns";

const DAY_MS = 86400000;
const ALL_KINDS = ["day", "week", "month", "quarter", "year", "custom"];

const KIND_LABELS = {
  day: "Day",
  week: "Week",
  month: "Month",
  quarter: "Quarter",
  year: "Year",
  custom: "Range",
};

const NARROW_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const toISO = (d) => format(d, "yyyy-MM-dd");
const startOfISOWeek = (d) => startOfWeek(d, { weekStartsOn: 1 });
const endOfISOWeek = (d) => endOfWeek(d, { weekStartsOn: 1 });

const monthGrid = (cursor) => {
  const first = startOfMonth(cursor);
  const start = startOfISOWeek(first);
  const cells = [];
  for (let i = 0; i < 42; i++) cells.push(addDays(start, i));
  return cells;
};

const weeksInYear = (year) => {
  const list = [];
  let m = startOfISOWeek(new Date(year, 0, 4));
  for (let i = 0; i < 60; i++) {
    const w = getISOWeek(m);
    const thursday = addDays(m, 3);
    if (thursday.getFullYear() > year) break;
    if (thursday.getFullYear() === year) list.push({ monday: m, w });
    m = addDays(m, 7);
  }
  return list;
};

const formatDragRange = (a, b) => {
  const [s, e] = a < b ? [a, b] : [b, a];
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth())
    return `${format(s, "MMM d")}-${format(e, "d, yyyy")}`;
  if (s.getFullYear() === e.getFullYear())
    return `${format(s, "MMM d")} - ${format(e, "MMM d, yyyy")}`;
  return `${format(s, "MMM d, yyyy")} - ${format(e, "MMM d, yyyy")}`;
};

// Public API: <PeriodSelector />
//   variant: "compact"  (default) — trigger pill that opens a centered popover
//            "timeline" — Visma-style horizontal year/month/week/day strip,
//                          rendered inline (no popover)
export const PeriodSelector = ({
  periodType = "month",
  baseDate,
  from,
  to,
  onChange,
  allowedPeriodTypes,
  variant = "compact",
  minYear,
  maxYear,
}) => {
  const { t } = useTranslation();

  const today = useMemo(
    () => (baseDate ? new Date(baseDate) : new Date()),
    [baseDate]
  );

  const allowed = useMemo(() => {
    if (Array.isArray(allowedPeriodTypes) && allowedPeriodTypes.length > 0) {
      return allowedPeriodTypes.filter((k) => ALL_KINDS.includes(k));
    }
    return ALL_KINDS;
  }, [allowedPeriodTypes]);

  const isAllowed = useCallback((k) => allowed.includes(k), [allowed]);

  // The "effective" kind — the periodType prop, but coerced to an allowed
  // value so the trigger and prev/next never operate on a disabled kind.
  const effectiveKind = useMemo(
    () => (allowed.includes(periodType) ? periodType : allowed[0]),
    [allowed, periodType]
  );

  // If the parent passes an out-of-allowed periodType, snap it back so the
  // header label and downstream consumers stay in sync.
  useEffect(() => {
    if (!from || !to) return;
    if (allowed.includes(periodType)) return;
    if (!onChange) return;
    onChange({ periodType: effectiveKind, from, to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed.join(","), periodType]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeKind, setActiveKind] = useState(effectiveKind);

  const initialCursor = useMemo(() => {
    if (from) return new Date(from);
    return today;
  }, [from, today]);
  const [cursor, setCursor] = useState(initialCursor);

  const [draftCustom, setDraftCustom] = useState(
    periodType === "custom" && from && to
      ? { start: new Date(from), end: new Date(to) }
      : null
  );

  useEffect(() => {
    if (dialogOpen) {
      const k = isAllowed(periodType) ? periodType : allowed[0];
      setActiveKind(k);
      setCursor(from ? new Date(from) : today);
      setDraftCustom(
        periodType === "custom" && from && to
          ? { start: new Date(from), end: new Date(to) }
          : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen]);

  useEffect(() => {
    if (!isAllowed(activeKind)) setActiveKind(allowed[0]);
  }, [allowed, activeKind, isAllowed]);

  const commit = useCallback(
    (kind, anchor) => {
      let f;
      let tt;
      switch (kind) {
        case "day":
          f = anchor;
          tt = anchor;
          break;
        case "week":
          f = startOfISOWeek(anchor);
          tt = endOfISOWeek(anchor);
          break;
        case "month":
          f = startOfMonth(anchor);
          tt = endOfMonth(anchor);
          break;
        case "quarter":
          f = startOfQuarter(anchor);
          tt = endOfQuarter(anchor);
          break;
        case "year":
          f = startOfYear(anchor);
          tt = endOfYear(anchor);
          break;
        case "custom": {
          let s = anchor.start;
          let e = anchor.end;
          if (e < s) {
            const tmp = s;
            s = e;
            e = tmp;
          }
          f = s;
          tt = e;
          break;
        }
        default:
          return;
      }
      if (onChange) {
        onChange({ periodType: kind, from: toISO(f), to: toISO(tt) });
      }
    },
    [onChange]
  );

  const getHeaderLabel = useCallback(() => {
    try {
      if (!from) return t("Select Period");
      const f = new Date(from);
      const tt = to ? new Date(to) : null;
      switch (effectiveKind) {
        case "year":
          return format(f, "yyyy");
        case "quarter": {
          const q = Math.floor(f.getMonth() / 3) + 1;
          return `Q${q} ${format(f, "yyyy")}`;
        }
        case "month":
          return format(f, "MMMM yyyy");
        case "week": {
          const wk = getISOWeek(f);
          return `${t("Week")} ${String(wk).padStart(2, "0")} - ${format(f, "yyyy")}`;
        }
        case "day":
          return format(f, "d. MMM yyyy");
        case "custom":
          if (tt) {
            if (f.getFullYear() === tt.getFullYear()) {
              if (f.getMonth() === tt.getMonth())
                return `${format(f, "d")}-${format(tt, "d. MMM yyyy")}`;
              return `${format(f, "d. MMM")} - ${format(tt, "d. MMM yyyy")}`;
            }
            return `${format(f, "d. MMM yyyy")} - ${format(tt, "d. MMM yyyy")}`;
          }
          return format(f, "d. MMM yyyy");
        default:
          if (tt)
            return `${format(f, "d. MMM yyyy")} - ${format(tt, "d. MMM yyyy")}`;
          return format(f, "d. MMM yyyy");
      }
    } catch (e) {
      return t("Select Period");
    }
  }, [from, to, effectiveKind, t]);

  const getHeaderSublabel = useCallback(() => {
    try {
      if (!from) return "";
      const f = new Date(from);
      const tt = to ? new Date(to) : null;
      if (effectiveKind === "week" && tt) {
        const sameMo = f.getMonth() === tt.getMonth();
        return sameMo
          ? `${format(f, "MMM d")}-${format(tt, "d")}`
          : `${format(f, "MMM d")} - ${format(tt, "MMM d")}`;
      }
      if (effectiveKind === "month" && tt) return format(f, "yyyy");
      if (effectiveKind === "quarter" && tt)
        return `${format(f, "MMM")}-${format(tt, "MMM")}`;
      if (effectiveKind === "year") return t("Year");
      if (effectiveKind === "day") return format(f, "EEEE");
      if (effectiveKind === "custom" && tt) {
        const days = differenceInCalendarDays(tt, f) + 1;
        return `${days} ${days === 1 ? t("day") : t("days")}`;
      }
      return "";
    } catch (e) {
      return "";
    }
  }, [from, to, effectiveKind, t]);

  const stepOuter = useCallback(
    (dir) => {
      if (!from) return;
      const sign = dir === "next" ? 1 : -1;
      const f = new Date(from);
      const tt = to ? new Date(to) : f;
      let na;
      let nb;
      switch (effectiveKind) {
        case "day":
          na = sign > 0 ? addDays(f, 1) : subDays(f, 1);
          nb = na;
          break;
        case "week": {
          const m = sign > 0 ? addWeeks(f, 1) : subWeeks(f, 1);
          na = startOfISOWeek(m);
          nb = endOfISOWeek(m);
          break;
        }
        case "month": {
          const m = sign > 0 ? addMonths(f, 1) : subMonths(f, 1);
          na = startOfMonth(m);
          nb = endOfMonth(m);
          break;
        }
        case "quarter": {
          const m = sign > 0 ? addQuarters(f, 1) : subQuarters(f, 1);
          na = startOfQuarter(m);
          nb = endOfQuarter(m);
          break;
        }
        case "year": {
          const m = sign > 0 ? addYears(f, 1) : subYears(f, 1);
          na = startOfYear(m);
          nb = endOfYear(m);
          break;
        }
        case "custom": {
          const span = differenceInCalendarDays(tt, f) + 1;
          na = sign > 0 ? addDays(f, span) : subDays(f, span);
          nb = sign > 0 ? addDays(tt, span) : subDays(tt, span);
          break;
        }
        default:
          return;
      }
      if (onChange) {
        onChange({ periodType: effectiveKind, from: toISO(na), to: toISO(nb) });
      }
    },
    [from, to, effectiveKind, onChange]
  );

  const navCursor = (dir) => {
    const sign = dir === "next" ? 1 : -1;
    switch (activeKind) {
      case "day":
      case "custom":
        setCursor((c) => addMonths(c, sign));
        break;
      case "week":
      case "month":
      case "quarter":
        setCursor((c) => addYears(c, sign));
        break;
      case "year":
        setCursor((c) => addYears(c, sign * 10));
        break;
      default:
        break;
    }
  };

  const handleShortcut = useCallback(() => {
    if (activeKind === "custom") {
      const t0 = today;
      setDraftCustom({ start: t0, end: t0 });
      setCursor(t0);
      return;
    }
    commit(activeKind, today);
    setDialogOpen(false);
  }, [activeKind, today, commit]);

  const shortcutLabel = useMemo(() => {
    switch (activeKind) {
      case "day":
        return t("Today");
      case "week":
        return t("This week");
      case "month":
        return t("This month");
      case "quarter":
        return t("This quarter");
      case "year":
        return t("This year");
      case "custom":
        return t("Reset");
      default:
        return t("Today");
    }
  }, [activeKind, t]);

  const handlePick = (anchor) => {
    if (activeKind === "custom") {
      setDraftCustom(anchor);
      return;
    }
    if (
      activeKind === "day" &&
      anchor &&
      anchor.start &&
      anchor.end &&
      !isSameDay(anchor.start, anchor.end)
    ) {
      if (isAllowed("custom")) {
        commit("custom", anchor);
        setDialogOpen(false);
        return;
      }
      commit("day", anchor.start);
      setDialogOpen(false);
      return;
    }
    const date = anchor && anchor.start ? anchor.start : anchor;
    commit(activeKind, date);
    setDialogOpen(false);
  };

  const applyCustom = () => {
    if (!draftCustom) return;
    commit("custom", draftCustom);
    setDialogOpen(false);
  };

  const innerHeader = useMemo(() => {
    if (activeKind === "day" || activeKind === "custom")
      return format(cursor, "MMMM yyyy");
    if (
      activeKind === "week" ||
      activeKind === "month" ||
      activeKind === "quarter"
    )
      return String(cursor.getFullYear());
    if (activeKind === "year") {
      const c = cursor.getFullYear();
      const start = c - (c % 10);
      return `${start} - ${start + 9}`;
    }
    return "";
  }, [activeKind, cursor]);

  // Timeline variant — Visma-style strip, rendered inline (no popover)
  if (variant === "timeline") {
    return (
      <TimelineStrip
        periodType={effectiveKind}
        from={from}
        to={to}
        today={today}
        onChange={onChange}
        allowed={allowed}
        isAllowed={isAllowed}
        minYear={minYear || today.getFullYear() - 1}
        maxYear={maxYear || today.getFullYear() + 1}
        t={t}
      />
    );
  }

  return (
    <div className="px-2 py-3">
      <div className="inline-flex items-center gap-1 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
        <button
          type="button"
          onClick={() => stepOuter("prev")}
          title={t("Previous")}
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
        >
          <ChevronLeft />
        </button>
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          title={t("Select Period")}
          className="group flex min-w-[220px] items-center justify-between gap-3 rounded-xl px-3 py-1.5 transition hover:bg-slate-50"
        >
          <span className="flex items-center gap-2.5">
            <KindIcon kind={effectiveKind} />
            <span className="flex flex-col items-start leading-tight">
              <span className="text-sm font-semibold text-slate-900">
                {getHeaderLabel()}
              </span>
              {getHeaderSublabel() && (
                <span className="text-[11px] font-medium text-slate-500">
                  {getHeaderSublabel()}
                </span>
              )}
            </span>
          </span>
          <ChevronDown />
        </button>
        <button
          type="button"
          onClick={() => stepOuter("next")}
          title={t("Next")}
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
        >
          <ChevronRight />
        </button>
      </div>

      <PMDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={t("Select Period")}
        size="md"
      >
        <div className="flex flex-col">
          <div className="flex items-center justify-between gap-3 px-1 pt-1 pb-3">
            <GranularitySegmented
              allowed={allowed}
              value={activeKind}
              onChange={(k) => {
                setActiveKind(k);
                if (k !== "custom") setDraftCustom(null);
              }}
              t={t}
            />
          </div>

          <div className="flex items-center justify-between px-1 pb-2">
            <button
              type="button"
              onClick={() => navCursor("prev")}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition hover:bg-blue-50 hover:text-slate-900"
              aria-label={t("Previous")}
            >
              <ChevronLeft />
            </button>
            <div className="text-sm font-semibold text-slate-900">
              {innerHeader}
            </div>
            <button
              type="button"
              onClick={() => navCursor("next")}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition hover:bg-blue-50 hover:text-slate-900"
              aria-label={t("Next")}
            >
              <ChevronRight />
            </button>
          </div>

          <div className="px-1 pb-3">
            {activeKind === "day" && isAllowed("day") && (
              <DayView
                cursor={cursor}
                today={today}
                from={from}
                to={to}
                periodType={periodType}
                onPick={handlePick}
                t={t}
              />
            )}
            {activeKind === "week" && isAllowed("week") && (
              <WeekView
                cursor={cursor}
                today={today}
                from={from}
                periodType={periodType}
                onPick={handlePick}
                t={t}
              />
            )}
            {activeKind === "month" && isAllowed("month") && (
              <MonthView
                cursor={cursor}
                today={today}
                from={from}
                periodType={periodType}
                onPick={handlePick}
                t={t}
              />
            )}
            {activeKind === "quarter" && isAllowed("quarter") && (
              <QuarterView
                cursor={cursor}
                today={today}
                from={from}
                periodType={periodType}
                onPick={handlePick}
                t={t}
              />
            )}
            {activeKind === "year" && isAllowed("year") && (
              <YearView
                cursor={cursor}
                today={today}
                from={from}
                periodType={periodType}
                onPick={handlePick}
              />
            )}
            {activeKind === "custom" && isAllowed("custom") && (
              <CustomView
                cursor={cursor}
                today={today}
                draft={draftCustom}
                setDraft={setDraftCustom}
                t={t}
              />
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-1 pt-3">
            <button
              type="button"
              onClick={handleShortcut}
              className="rounded-lg px-3 py-1.5 text-sm font-semibold text-blue-600 transition hover:bg-blue-50"
            >
              {shortcutLabel}
            </button>
            {activeKind === "custom" ? (
              <button
                type="button"
                onClick={applyCustom}
                disabled={!draftCustom || !draftCustom.start || !draftCustom.end}
                className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
              >
                {t("Apply")}
              </button>
            ) : (
              <span className="text-xs font-medium text-slate-500 tabular-nums">
                {from && to
                  ? `${format(new Date(from), "d MMM")} - ${format(new Date(to), "d MMM yyyy")}`
                  : "-"}
              </span>
            )}
          </div>
        </div>
      </PMDialog>
    </div>
  );
};

const GranularitySegmented = ({ allowed, value, onChange, t }) => {
  if (allowed.length <= 1) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-blue-700">
        <LockIcon />
        {t("Locked to")} {t(KIND_LABELS[allowed[0]])}
      </span>
    );
  }
  return (
    <div className="inline-flex rounded-xl bg-slate-100 p-1">
      {allowed.map((k) => {
        const active = k === value;
        return (
          <button
            key={k}
            type="button"
            onClick={() => onChange(k)}
            className={`rounded-lg px-3 py-1 text-xs font-semibold transition ${
              active
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t(KIND_LABELS[k])}
          </button>
        );
      })}
    </div>
  );
};

// Day view — refs (not state) for drag tracking so the global mouseup
// listener always reads current values. This fixes single-click being
// dropped if the user releases before React re-renders.
const DayView = ({ cursor, today, from, to, periodType, onPick, t }) => {
  const cells = monthGrid(cursor);
  const dragStartRef = useRef(null);
  const dragEndRef = useRef(null);
  const movedRef = useRef(false);
  const [dragVisual, setDragVisual] = useState({ start: null, end: null });

  useEffect(() => {
    const up = () => {
      const s = dragStartRef.current;
      if (!s) return;
      const e = dragEndRef.current || s;
      const wasMoved = movedRef.current;
      dragStartRef.current = null;
      dragEndRef.current = null;
      movedRef.current = false;
      setDragVisual({ start: null, end: null });
      if (isSameDay(s, e) && !wasMoved) {
        onPick(s);
      } else {
        let a = s;
        let b = e;
        if (b < a) {
          const tmp = a;
          a = b;
          b = tmp;
        }
        onPick({ start: a, end: b });
      }
    };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, [onPick]);

  const dragStart = dragVisual.start;
  const dragEnd = dragVisual.end;
  const inDrag = (d) => {
    if (!dragStart) return false;
    const e = dragEnd || dragStart;
    const a = dragStart < e ? dragStart : e;
    const b = dragStart < e ? e : dragStart;
    return d >= a && d <= b;
  };
  const isDragRange = !!(
    dragStart &&
    dragEnd &&
    !isSameDay(dragStart, dragEnd)
  );
  const isDragStart = (d) =>
    dragStart &&
    isSameDay(d, dragStart < (dragEnd || dragStart) ? dragStart : dragEnd);
  const isDragEnd = (d) =>
    dragEnd && isSameDay(d, dragStart < dragEnd ? dragEnd : dragStart);

  const valIsCustom = !!from && !!to && periodType === "custom";
  const valIsDay = !!from && periodType === "day";
  const valFrom = from ? new Date(from) : null;
  const valTo = to ? new Date(to) : null;
  const inExisting = (d) => valIsCustom && d >= valFrom && d <= valTo;

  const weekdays = [t("M"), t("T"), t("W"), t("T"), t("F"), t("S"), t("S")];

  return (
    <div className="select-none">
      <div className="grid grid-cols-7 px-0.5 pb-1">
        {weekdays.map((w, i) => (
          <div
            key={i}
            className="py-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400"
          >
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((d, i) => {
          const muted = !isSameMonth(d, cursor);
          const weekend = d.getDay() === 0 || d.getDay() === 6;
          const inDr = inDrag(d);
          const inEx = !dragStart && inExisting(d);
          const dragStartCell = isDragRange && isDragStart(d);
          const dragEndCell = isDragRange && isDragEnd(d);
          const exStartCell = inEx && isSameDay(d, valFrom);
          const exEndCell = inEx && isSameDay(d, valTo);
          const isPillSel =
            (valIsDay && isSameDay(valFrom, d)) ||
            dragStartCell ||
            dragEndCell ||
            exStartCell ||
            exEndCell;
          const inMidRange =
            (isDragRange && inDr && !dragStartCell && !dragEndCell) ||
            (inEx && !exStartCell && !exEndCell);
          const isRangeStart = dragStartCell || exStartCell;
          const isRangeEnd = dragEndCell || exEndCell;
          const radL =
            isRangeStart || (!isDragRange && !inEx) ? 999 : inMidRange ? 0 : 999;
          const radR =
            isRangeEnd || (!isDragRange && !inEx) ? 999 : inMidRange ? 0 : 999;
          const isToday = isSameDay(d, today);
          let textCls = "text-slate-900";
          if (isPillSel) textCls = "text-white";
          else if (muted) textCls = "text-slate-300";
          else if (weekend) textCls = "text-slate-500";
          return (
            <div
              key={i}
              className="relative"
              onMouseDown={(e) => {
                e.preventDefault();
                movedRef.current = false;
                dragStartRef.current = d;
                dragEndRef.current = d;
                setDragVisual({ start: d, end: d });
              }}
              onMouseEnter={() => {
                if (dragStartRef.current) {
                  if (!isSameDay(d, dragStartRef.current)) movedRef.current = true;
                  dragEndRef.current = d;
                  setDragVisual({ start: dragStartRef.current, end: d });
                }
              }}
            >
              {inMidRange && (
                <div className="pointer-events-none absolute inset-y-0.5 inset-x-0 bg-blue-100" />
              )}
              <button
                type="button"
                tabIndex={-1}
                style={{
                  borderRadius: `${radL}px ${radR}px ${radR}px ${radL}px`,
                  background: isPillSel ? "#3b82f6" : "transparent",
                  boxShadow: isPillSel
                    ? "0 2px 8px rgba(59,130,246,0.35)"
                    : isToday && !isPillSel
                    ? "inset 0 0 0 1.5px #3b82f6"
                    : "none",
                }}
                className={`relative flex h-9 w-full items-center justify-center text-[13px] font-medium tabular-nums transition ${textCls} ${
                  !isPillSel && !inMidRange ? "hover:bg-blue-50" : ""
                }`}
              >
                {d.getDate()}
              </button>
            </div>
          );
        })}
      </div>
      <div className="mt-2 text-center text-[11px] font-medium text-slate-400">
        {isDragRange
          ? formatDragRange(dragStart, dragEnd)
          : t("Click a day, drag to pick a range")}
      </div>
    </div>
  );
};

const WeekView = ({ cursor, today, from, periodType, onPick, t }) => {
  const year = cursor.getFullYear();
  const weeks = useMemo(() => weeksInYear(year), [year]);
  const todayWeek = getISOWeek(today);
  const todayWeekYear = (() => {
    const thursday = addDays(startOfISOWeek(today), 3);
    return thursday.getFullYear();
  })();
  const fromDate = from ? new Date(from) : null;
  const isWeekSelected =
    fromDate && periodType === "week"
      ? (mon) => isSameDay(startOfISOWeek(fromDate), mon)
      : () => false;

  return (
    <div>
      <div className="grid grid-cols-6 gap-1.5">
        {weeks.map(({ monday, w }) => {
          const isSel = isWeekSelected(monday);
          const isToday = todayWeekYear === year && todayWeek === w;
          const sub = format(monday, "MMM d");
          return (
            <button
              key={`${year}-${w}`}
              type="button"
              onClick={() => onPick(monday)}
              style={{
                background: isSel ? "#3b82f6" : "transparent",
                boxShadow: isSel
                  ? "0 2px 8px rgba(59,130,246,0.35)"
                  : isToday && !isSel
                  ? "inset 0 0 0 1.5px #3b82f6"
                  : "none",
              }}
              className={`flex h-11 flex-col items-center justify-center rounded-full text-[13px] font-medium tabular-nums transition ${
                isSel ? "text-white" : "text-slate-900 hover:bg-blue-50"
              }`}
            >
              <span className="text-[13px] leading-none">
                W{String(w).padStart(2, "0")}
              </span>
              <span
                className={`text-[9.5px] leading-none ${
                  isSel ? "opacity-90" : "text-slate-500"
                }`}
              >
                {sub}
              </span>
            </button>
          );
        })}
      </div>
      <div className="mt-2 text-center text-[11px] font-medium text-slate-400">
        {weeks.length} {t("weeks")} - ISO 8601 - {t("Mon-Sun")}
      </div>
    </div>
  );
};

const MonthView = ({ cursor, today, from, periodType, onPick, t }) => {
  const year = cursor.getFullYear();
  const fromDate = from ? new Date(from) : null;
  const monthLabels = NARROW_MONTHS.map((m) => t(m));
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {monthLabels.map((m, i) => {
        const d = new Date(year, i, 1);
        const isSel =
          fromDate &&
          periodType === "month" &&
          fromDate.getFullYear() === year &&
          fromDate.getMonth() === i;
        const isToday = today.getFullYear() === year && today.getMonth() === i;
        return (
          <button
            key={m}
            type="button"
            onClick={() => onPick(d)}
            style={{
              background: isSel ? "#3b82f6" : "transparent",
              boxShadow: isSel
                ? "0 2px 8px rgba(59,130,246,0.35)"
                : isToday && !isSel
                ? "inset 0 0 0 1.5px #3b82f6"
                : "none",
            }}
            className={`flex h-14 items-center justify-center rounded-full text-sm font-semibold transition ${
              isSel ? "text-white" : "text-slate-900 hover:bg-blue-50"
            }`}
          >
            {m}
          </button>
        );
      })}
    </div>
  );
};

const QuarterView = ({ cursor, today, from, periodType, onPick, t }) => {
  const year = cursor.getFullYear();
  const fromDate = from ? new Date(from) : null;
  const tQ = Math.floor(today.getMonth() / 3);
  const monthsShort = NARROW_MONTHS.map((m) => t(m));
  return (
    <div className="grid grid-cols-2 gap-2">
      {[0, 1, 2, 3].map((q) => {
        const start = new Date(year, q * 3, 1);
        const isSel =
          fromDate &&
          periodType === "quarter" &&
          fromDate.getFullYear() === year &&
          Math.floor(fromDate.getMonth() / 3) === q;
        const isToday = today.getFullYear() === year && tQ === q;
        return (
          <button
            key={q}
            type="button"
            onClick={() => onPick(start)}
            style={{
              background: isSel ? "#3b82f6" : "transparent",
              boxShadow: isSel
                ? "0 2px 8px rgba(59,130,246,0.35)"
                : isToday && !isSel
                ? "inset 0 0 0 1.5px #3b82f6"
                : "none",
            }}
            className={`flex h-[68px] flex-col items-center justify-center rounded-2xl text-base font-semibold transition ${
              isSel ? "text-white" : "text-slate-900 hover:bg-blue-50"
            }`}
          >
            <span className="text-lg font-bold leading-none">Q{q + 1}</span>
            <span
              className={`mt-1 text-[10.5px] font-medium leading-none ${
                isSel ? "opacity-90" : "text-slate-500"
              }`}
            >
              {monthsShort[q * 3]}-{monthsShort[q * 3 + 2]}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const YearView = ({ cursor, today, from, periodType, onPick }) => {
  const center = cursor.getFullYear();
  const start = center - (center % 10) - 1;
  const years = Array.from({ length: 12 }, (_, i) => start + i);
  const fromDate = from ? new Date(from) : null;
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {years.map((y) => {
        const d = new Date(y, 0, 1);
        const muted = y < start + 1 || y > start + 10;
        const isSel =
          fromDate && periodType === "year" && fromDate.getFullYear() === y;
        const isToday = today.getFullYear() === y;
        let textCls = "text-slate-900";
        if (isSel) textCls = "text-white";
        else if (muted) textCls = "text-slate-300";
        return (
          <button
            key={y}
            type="button"
            onClick={() => onPick(d)}
            style={{
              background: isSel ? "#3b82f6" : "transparent",
              boxShadow: isSel
                ? "0 2px 8px rgba(59,130,246,0.35)"
                : isToday && !isSel
                ? "inset 0 0 0 1.5px #3b82f6"
                : "none",
            }}
            className={`flex h-14 items-center justify-center rounded-full text-sm font-bold tabular-nums transition ${textCls} ${
              !isSel ? "hover:bg-blue-50" : ""
            }`}
          >
            {y}
          </button>
        );
      })}
    </div>
  );
};

const CustomView = ({ cursor, today, draft, setDraft, t }) => {
  const [hover, setHover] = useState(null);
  const dragging = useRef(false);
  const dragMoved = useRef(false);

  useEffect(() => {
    const up = () => {
      if (!dragging.current) return;
      dragging.current = false;
      if (dragMoved.current && draft && draft.start && draft.end) {
        let s = draft.start;
        let e = draft.end;
        if (e < s) {
          const tmp = s;
          s = e;
          e = tmp;
        }
        setDraft({ start: s, end: e });
      }
      dragMoved.current = false;
    };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, [draft, setDraft]);

  const handleClick = (d) => {
    if (dragMoved.current) return;
    if (!draft || (draft && draft.start && draft.end)) {
      setDraft({ start: d, end: null });
      return;
    }
    let s = draft.start;
    let e = d;
    if (e < s) {
      const tmp = s;
      s = e;
      e = tmp;
    }
    setDraft({ start: s, end: e });
  };

  const handleMouseDown = (d, e) => {
    e.preventDefault();
    dragging.current = true;
    dragMoved.current = false;
    setDraft({ start: d, end: d });
  };

  const handleMouseEnter = (d) => {
    setHover(d);
    if (dragging.current && draft) {
      if (!isSameDay(d, draft.start)) dragMoved.current = true;
      setDraft({ ...draft, end: d });
    }
  };

  const draftStart = draft ? draft.start : null;
  const draftEnd = draft ? draft.end : null;
  const tentEnd = draftEnd || hover;
  const cells = monthGrid(cursor);

  const inRange = (d) => {
    if (!draftStart || !tentEnd) return false;
    const s = draftStart < tentEnd ? draftStart : tentEnd;
    const e = draftStart < tentEnd ? tentEnd : draftStart;
    return d >= s && d <= e;
  };
  const isStart = (d) =>
    draftStart &&
    isSameDay(d, draftStart) &&
    (!draftEnd || draftEnd >= draftStart);
  const isEnd = (d) => draftEnd && isSameDay(d, draftEnd);

  const weekdays = [t("M"), t("T"), t("W"), t("T"), t("F"), t("S"), t("S")];

  return (
    <div onMouseLeave={() => setHover(null)} className="select-none">
      <div className="grid grid-cols-7 px-0.5 pb-1">
        {weekdays.map((w, i) => (
          <div
            key={i}
            className="py-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400"
          >
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((d, i) => {
          const muted = !isSameMonth(d, cursor);
          const sel = isStart(d) || isEnd(d);
          const inR = inRange(d);
          const radL = isStart(d) ? 999 : inR ? 0 : 999;
          const radR =
            isEnd(d) ||
            (draftStart && !draftEnd && hover && isSameDay(d, hover))
              ? 999
              : inR
              ? 0
              : 999;
          const isToday = isSameDay(d, today);
          let textCls = "text-slate-900";
          if (sel) textCls = "text-white";
          else if (muted) textCls = "text-slate-300";
          return (
            <div
              key={i}
              className="relative"
              onMouseDown={(e) => handleMouseDown(d, e)}
              onMouseEnter={() => handleMouseEnter(d)}
            >
              {inR && !sel && (
                <div className="pointer-events-none absolute inset-y-0.5 inset-x-0 bg-blue-100" />
              )}
              <button
                type="button"
                onClick={() => handleClick(d)}
                style={{
                  borderRadius: `${radL}px ${radR}px ${radR}px ${radL}px`,
                  background: sel ? "#3b82f6" : "transparent",
                  boxShadow: sel
                    ? "0 2px 8px rgba(59,130,246,0.35)"
                    : isToday && !sel
                    ? "inset 0 0 0 1.5px #3b82f6"
                    : "none",
                }}
                className={`relative flex h-9 w-full items-center justify-center text-[13px] font-medium tabular-nums transition ${textCls} ${
                  !sel && !inR ? "hover:bg-blue-50" : ""
                }`}
              >
                {d.getDate()}
              </button>
            </div>
          );
        })}
      </div>
      <div className="mt-2 text-center text-[11px] font-medium text-slate-400">
        {draftStart && draftEnd && !isSameDay(draftStart, draftEnd)
          ? `${formatDragRange(draftStart, draftEnd)} - ${differenceInCalendarDays(draftEnd, draftStart) + 1} ${t("days")}`
          : draftStart && !draftEnd
          ? t("Pick the end day, or drag")
          : t("Click a day or drag to select a range")}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Timeline variant — Visma-style horizontal strip
// Year/Month/Week/Day tracks. Click any cell to snap; drag empty space to
// draw a custom range; grab band to move; drag edge handles to resize.
// ─────────────────────────────────────────────────────────────────────────
const TimelineStrip = ({
  periodType,
  from,
  to,
  today,
  onChange,
  allowed,
  isAllowed,
  minYear,
  maxYear,
  t,
}) => {
  const DAY_W = 5.2;
  const ROW_H = 26;

  const start0 = useMemo(() => new Date(minYear, 0, 1), [minYear]);
  const end0 = useMemo(() => new Date(maxYear + 1, 0, 1), [maxYear]);
  const totalDays = Math.round((end0 - start0) / DAY_MS);
  const totalW = totalDays * DAY_W;

  const dayToX = useCallback(
    (d) => {
      const dd = new Date(d);
      dd.setHours(0, 0, 0, 0);
      return Math.round((dd - start0) / DAY_MS) * DAY_W;
    },
    [start0]
  );
  const xToDay = useCallback(
    (x) =>
      addDays(
        start0,
        Math.max(0, Math.min(totalDays - 1, Math.round(x / DAY_W)))
      ),
    [start0, totalDays]
  );

  const scrollerRef = useRef(null);

  const range = useMemo(() => {
    if (!from || !to) return null;
    return { start: new Date(from), end: new Date(to) };
  }, [from, to]);

  useEffect(() => {
    if (!scrollerRef.current || !range) return;
    const x = dayToX(range.start);
    scrollerRef.current.scrollLeft = Math.max(0, x - 220);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [draftRange, setDraftRange] = useState(null);
  const drag = useRef({ kind: null });

  const visibleRange = draftRange || range;

  const commit = useCallback(
    (r, kindHint) => {
      if (!onChange) return;
      let s = r.start;
      let e = r.end;
      if (e < s) {
        const tmp = s;
        s = e;
        e = tmp;
      }
      let kind = kindHint || "custom";
      if (!isAllowed(kind)) kind = allowed[0];
      onChange({ periodType: kind, from: toISO(s), to: toISO(e) });
    },
    [onChange, isAllowed, allowed]
  );

  const bodyDown = (e) => {
    if (drag.current.kind) return;
    if (!isAllowed("custom") && !isAllowed("day")) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left + scrollerRef.current.scrollLeft;
    const d = xToDay(x);
    drag.current = {
      kind: "new",
      anchorDay: d,
      originStart: d,
      originEnd: d,
      intent: isAllowed("custom") ? "custom" : "day",
    };
    setDraftRange({ start: d, end: d });
    e.preventDefault();
  };

  const handleStartEdgeDown = (e) => {
    if (!visibleRange) return;
    e.stopPropagation();
    e.preventDefault();
    drag.current = {
      kind: "resize-start",
      originStart: visibleRange.start,
      originEnd: visibleRange.end,
      intent: "custom",
    };
  };
  const handleEndEdgeDown = (e) => {
    if (!visibleRange) return;
    e.stopPropagation();
    e.preventDefault();
    drag.current = {
      kind: "resize-end",
      originStart: visibleRange.start,
      originEnd: visibleRange.end,
      intent: "custom",
    };
  };
  const handleBandDown = (e) => {
    if (!visibleRange) return;
    e.stopPropagation();
    e.preventDefault();
    const rect = scrollerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + scrollerRef.current.scrollLeft;
    const d = xToDay(x);
    drag.current = {
      kind: "move",
      anchorDay: d,
      originStart: visibleRange.start,
      originEnd: visibleRange.end,
      intent: periodType || "custom",
    };
  };

  useEffect(() => {
    const move = (e) => {
      if (!drag.current.kind) return;
      const scroller = scrollerRef.current;
      if (!scroller) return;
      const rect = scroller.getBoundingClientRect();
      const x = e.clientX - rect.left + scroller.scrollLeft;
      const d = xToDay(x);
      const D = drag.current;
      if (D.kind === "new") {
        let s = D.anchorDay;
        let ee = d;
        if (ee < s) {
          const tmp = s;
          s = ee;
          ee = tmp;
        }
        setDraftRange({ start: s, end: ee });
      } else if (D.kind === "resize-start") {
        let s = d;
        const ee = D.originEnd;
        if (s > ee) s = ee;
        setDraftRange({ start: s, end: ee });
      } else if (D.kind === "resize-end") {
        const s = D.originStart;
        let ee = d;
        if (ee < s) ee = s;
        setDraftRange({ start: s, end: ee });
      } else if (D.kind === "move") {
        const offsetDays = Math.round((d - D.anchorDay) / DAY_MS);
        const ns = addDays(D.originStart, offsetDays);
        const ne = addDays(D.originEnd, offsetDays);
        setDraftRange({ start: ns, end: ne });
      }
    };
    const up = () => {
      if (!drag.current.kind) return;
      const D = drag.current;
      if (draftRange) {
        let intent = D.intent || "custom";
        if (
          D.kind === "new" &&
          isSameDay(draftRange.start, draftRange.end) &&
          isAllowed("day")
        ) {
          intent = "day";
        }
        commit(draftRange, intent);
      }
      drag.current = { kind: null };
      setDraftRange(null);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [draftRange, commit, xToDay, isAllowed]);

  const years = [];
  for (let y = minYear; y <= maxYear; y++) {
    const ys = new Date(y, 0, 1);
    const ye = new Date(y + 1, 0, 1);
    years.push({ y, x: dayToX(ys), w: dayToX(ye) - dayToX(ys) });
  }
  const months = [];
  for (let y = minYear; y <= maxYear; y++) {
    for (let m = 0; m < 12; m++) {
      const ms = new Date(y, m, 1);
      const me = new Date(y, m + 1, 1);
      months.push({ y, m, x: dayToX(ms), w: dayToX(me) - dayToX(ms) });
    }
  }
  const weeks = [];
  let wcur = startOfISOWeek(start0);
  while (wcur < end0) {
    const wn = getISOWeek(wcur);
    weeks.push({ x: dayToX(wcur), w: 7 * DAY_W, week: wn, monday: wcur });
    wcur = addDays(wcur, 7);
  }
  const days = [];
  let dcur = new Date(start0);
  while (dcur < end0) {
    const wd = (dcur.getDay() + 6) % 7;
    days.push({ x: dayToX(dcur), w: DAY_W, d: new Date(dcur), n: dcur.getDate(), wd });
    dcur = addDays(dcur, 1);
  }

  const rangeX = visibleRange ? dayToX(visibleRange.start) : 0;
  const rangeW = visibleRange ? dayToX(addDays(visibleRange.end, 1)) - rangeX : 0;
  const todayX = dayToX(today);

  const monthsLocalized = NARROW_MONTHS.map((m) => t(m));

  const pickRange = (s, e, kindHint) => {
    drag.current = { kind: null };
    setDraftRange(null);
    let kind = kindHint || "custom";
    if (!isAllowed(kind)) kind = allowed[0];
    if (onChange) onChange({ periodType: kind, from: toISO(s), to: toISO(e) });
  };
  const stopAndPick = (s, e, kindHint) => (ev) => {
    if (!isAllowed(kindHint)) return;
    ev.stopPropagation();
    ev.preventDefault();
    pickRange(s, e, kindHint);
  };

  const scrollTo = (d) => {
    if (!scrollerRef.current) return;
    const x = dayToX(d);
    scrollerRef.current.scrollTo({ left: Math.max(0, x - 240), behavior: "smooth" });
  };

  const trackBtn = {
    position: "absolute",
    top: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "inherit",
    cursor: "pointer",
    padding: 0,
    border: "none",
    borderRight: "1px solid #f1f5f9",
    background: "transparent",
  };
  const disabledStyle = { cursor: "not-allowed", opacity: 0.45 };

  return (
    <div className="flex w-full flex-col">
      <div className="mb-2 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {t("From")}
          </span>
          <TimelineDateInput
            value={visibleRange ? visibleRange.start : today}
            onChange={(d) => {
              if (visibleRange) {
                const e = visibleRange.end < d ? d : visibleRange.end;
                commit({ start: d, end: e }, "custom");
              } else {
                commit({ start: d, end: d }, "day");
              }
            }}
          />
        </div>
        <div className="pb-2 text-slate-400">→</div>
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {t("To")}
          </span>
          <TimelineDateInput
            value={visibleRange ? visibleRange.end : today}
            onChange={(d) => {
              if (visibleRange) {
                const s = visibleRange.start > d ? d : visibleRange.start;
                commit({ start: s, end: d }, "custom");
              } else {
                commit({ start: d, end: d }, "day");
              }
            }}
          />
        </div>
        <div className="ml-auto flex items-end gap-1.5">
          {isAllowed("day") && (
            <TimelineQuickBtn
              onClick={() => {
                pickRange(today, today, "day");
                scrollTo(today);
              }}
            >
              {t("Today")}
            </TimelineQuickBtn>
          )}
          {isAllowed("month") && (
            <TimelineQuickBtn
              onClick={() => {
                const s = startOfMonth(today);
                const e = endOfMonth(today);
                pickRange(s, e, "month");
                scrollTo(s);
              }}
            >
              {t("This month")}
            </TimelineQuickBtn>
          )}
          {isAllowed("year") && (
            <TimelineQuickBtn
              onClick={() => {
                const s = startOfYear(today);
                const e = endOfYear(today);
                pickRange(s, e, "year");
                scrollTo(s);
              }}
            >
              {t("This year")}
            </TimelineQuickBtn>
          )}
        </div>
      </div>

      <div
        ref={scrollerRef}
        onMouseDown={bodyDown}
        className="relative w-full select-none overflow-x-auto overflow-y-hidden rounded-xl border border-slate-200 bg-white"
        style={{
          cursor: !isAllowed("custom") && !isAllowed("day") ? "default" : "crosshair",
        }}
      >
        <div style={{ position: "relative", width: totalW, height: ROW_H * 4 + 8 }}>
          {/* Year row */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: totalW,
              height: ROW_H,
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            {years.map(({ y, x, w }) => (
              <button
                key={y}
                type="button"
                onMouseDown={stopAndPick(new Date(y, 0, 1), new Date(y, 11, 31), "year")}
                disabled={!isAllowed("year")}
                style={{
                  ...trackBtn,
                  left: x,
                  width: w,
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: "#0f172a",
                  background: y % 2 === 0 ? "transparent" : "#f1f5f9",
                  ...(isAllowed("year") ? {} : disabledStyle),
                }}
                onMouseEnter={(e) => {
                  if (isAllowed("year")) e.currentTarget.style.background = "#dbeafe";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    y % 2 === 0 ? "transparent" : "#f1f5f9";
                }}
              >
                {y}
              </button>
            ))}
          </div>
          {/* Month row */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: ROW_H,
              width: totalW,
              height: ROW_H,
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            {months.map(({ y, m, x, w }) => (
              <button
                key={`${y}-${m}`}
                type="button"
                onMouseDown={stopAndPick(new Date(y, m, 1), new Date(y, m + 1, 0), "month")}
                disabled={!isAllowed("month")}
                style={{
                  ...trackBtn,
                  left: x,
                  width: w,
                  fontSize: 10.5,
                  color: "#475569",
                  fontWeight: 500,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  ...(isAllowed("month") ? {} : disabledStyle),
                }}
                onMouseEnter={(e) => {
                  if (isAllowed("month")) e.currentTarget.style.background = "#dbeafe";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                {w > 38 ? monthsLocalized[m] : monthsLocalized[m].slice(0, 1)}
              </button>
            ))}
          </div>
          {/* Week row */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: ROW_H * 2,
              width: totalW,
              height: ROW_H,
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            {weeks.map((wk, i) => {
              const we = addDays(wk.monday, 6);
              return (
                <button
                  key={i}
                  type="button"
                  onMouseDown={stopAndPick(wk.monday, we, "week")}
                  disabled={!isAllowed("week")}
                  style={{
                    ...trackBtn,
                    left: wk.x,
                    width: wk.w,
                    fontSize: 9.5,
                    color: "#64748b",
                    fontWeight: 500,
                    fontVariantNumeric: "tabular-nums",
                    ...(isAllowed("week") ? {} : disabledStyle),
                  }}
                  onMouseEnter={(e) => {
                    if (isAllowed("week")) e.currentTarget.style.background = "#dbeafe";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  {wk.w > 18 ? wk.week : ""}
                </button>
              );
            })}
          </div>
          {/* Day row */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: ROW_H * 3,
              width: totalW,
              height: ROW_H,
            }}
          >
            {days.map((dy, i) => (
              <button
                key={i}
                type="button"
                onMouseDown={stopAndPick(dy.d, dy.d, "day")}
                disabled={!isAllowed("day")}
                style={{
                  ...trackBtn,
                  left: dy.x,
                  width: dy.w,
                  fontSize: 9,
                  color: dy.wd >= 5 ? "#94a3b8" : "#64748b",
                  fontWeight: 500,
                  borderRight: dy.n === 1 ? "1px solid #e2e8f0" : "none",
                  background: dy.wd >= 5 ? "rgba(0,0,0,.02)" : "transparent",
                  fontVariantNumeric: "tabular-nums",
                  overflow: "hidden",
                  ...(isAllowed("day") ? {} : disabledStyle),
                }}
                onMouseEnter={(e) => {
                  if (isAllowed("day")) e.currentTarget.style.background = "#dbeafe";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    dy.wd >= 5 ? "rgba(0,0,0,.02)" : "transparent";
                }}
              >
                {dy.w >= 14 ? dy.n : ""}
              </button>
            ))}
          </div>

          <div
            style={{
              position: "absolute",
              left: todayX,
              top: 0,
              bottom: 0,
              width: 0,
              borderLeft: "1.5px dashed #3b82f6",
              pointerEvents: "none",
              opacity: 0.6,
            }}
          />

          {visibleRange && (
            <div
              onMouseDown={handleBandDown}
              style={{
                position: "absolute",
                left: rangeX,
                top: 0,
                width: rangeW,
                height: "100%",
                background: "rgba(59,130,246,0.18)",
                border: "1.5px solid #3b82f6",
                borderRadius: 6,
                cursor: drag.current.kind === "move" ? "grabbing" : "grab",
                boxShadow: "0 2px 12px rgba(59,130,246,0.2)",
              }}
            >
              <div
                onMouseDown={handleStartEdgeDown}
                style={{
                  position: "absolute",
                  left: -4,
                  top: 0,
                  bottom: 0,
                  width: 9,
                  cursor: "ew-resize",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: 3,
                    height: 24,
                    background: "#3b82f6",
                    borderRadius: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,.2)",
                  }}
                />
              </div>
              <div
                onMouseDown={handleEndEdgeDown}
                style={{
                  position: "absolute",
                  right: -4,
                  top: 0,
                  bottom: 0,
                  width: 9,
                  cursor: "ew-resize",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: 3,
                    height: 24,
                    background: "#3b82f6",
                    borderRadius: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,.2)",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 text-[11px] text-slate-500">
        <span>
          {t("Click any year, month, week or day, drag empty space to draw a range, grab the band to move")}
        </span>
        {visibleRange && (
          <span className="font-semibold text-slate-900">
            {formatDragRange(visibleRange.start, visibleRange.end)}
            {" - "}
            {Math.round((visibleRange.end - visibleRange.start) / DAY_MS) + 1}{" "}
            {t("days")}
          </span>
        )}
      </div>
    </div>
  );
};

const TimelineDateInput = ({ value, onChange }) => {
  const v = `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
  return (
    <input
      type="date"
      value={v}
      onChange={(e) => {
        const parts = e.target.value.split("-").map(Number);
        if (parts.length === 3 && parts.every((p) => p)) {
          onChange(new Date(parts[0], parts[1] - 1, parts[2]));
        }
      }}
      className="h-[30px] min-w-[130px] rounded-lg border border-slate-200 bg-white px-2.5 text-[12.5px] tabular-nums text-slate-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
    />
  );
};

const TimelineQuickBtn = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="h-[30px] rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
  >
    {children}
  </button>
);

const ChevronLeft = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ChevronDown = ({ className = "text-slate-400" }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={className}>
    <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const LockIcon = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
    <path d="M3.5 5.5V4a2.5 2.5 0 015 0v1.5M3 5.5h6v4.5H3z" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);
const KindIcon = ({ kind }) => (
  <span
    aria-hidden
    className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-700"
  >
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      {kind === "day" && (
        <>
          <rect x="2.5" y="3.5" width="11" height="10" rx="2" stroke="currentColor" strokeWidth="1.4" />
          <circle cx="8" cy="9" r="1.4" fill="currentColor" />
        </>
      )}
      {kind === "week" && (
        <>
          <rect x="2.5" y="3.5" width="11" height="10" rx="2" stroke="currentColor" strokeWidth="1.4" />
          <rect x="4.5" y="7" width="7" height="3" rx="1" fill="currentColor" />
        </>
      )}
      {kind === "month" && (
        <>
          <rect x="2.5" y="3.5" width="11" height="10" rx="2" stroke="currentColor" strokeWidth="1.4" />
          <path d="M2.5 7h11" stroke="currentColor" strokeWidth="1.4" />
        </>
      )}
      {kind === "quarter" && (
        <>
          <rect x="2.5" y="2.5" width="5" height="5" rx="1" fill="currentColor" />
          <rect x="8.5" y="2.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
          <rect x="2.5" y="8.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
          <rect x="8.5" y="8.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
        </>
      )}
      {kind === "year" && (
        <>
          <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.4" />
          <path d="M8 4v4l2.5 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </>
      )}
      {kind === "custom" && (
        <path d="M3 8h10M3 8l3-3M3 8l3 3M13 8l-3-3M13 8l-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  </span>
);
